import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    InputAdornment,
    ToggleButton,
    ToggleButtonGroup,
    Grid,
    Fade,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Avatar,
    Chip,
    IconButton,
    Card,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Alert,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Snackbar,
    Menu,
    TablePagination,
    Paper
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EditIcon from '@mui/icons-material/Edit';
import LockResetIcon from '@mui/icons-material/LockReset';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import { theme } from '../../../theme';
import UserCard from '../components/UserCard';
import { superadminService } from '../../../services/superadmin.service';
import { useAuth } from '../../../context/AuthContext';

const UsersList = () => {
    const { user: currentUser } = useAuth();
    const [viewMode, setViewMode] = useState('card');
    const [search, setSearch] = useState('');
    const [submittedSearch, setSubmittedSearch] = useState('');
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [total, setTotal] = useState(0);

    // Dialog States
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogMode, setDialogMode] = useState('create'); // 'create' or 'edit'
    const [selectedUser, setSelectedUser] = useState(null);
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        role: 'admin',
        is_active: true
    });
    const [saving, setSaving] = useState(false);
    const [dialogError, setDialogError] = useState('');

    // Reset Password State
    const [resetOpen, setResetOpen] = useState(false);
    const [resetUser, setResetUser] = useState(null);
    const [resetLoading, setResetLoading] = useState(false);

    // Delete User State
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteUser, setDeleteUser] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Toast State
    const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

    // Action Menu State (for list view)
    const [anchorEl, setAnchorEl] = useState(null);
    const [menuUser, setMenuUser] = useState(null);

    const showToast = (message, severity = 'success') => {
        setToast({ open: true, message, severity });
    };

    const canDeleteUser = (targetUser) => {
        return !(currentUser?.role === 'admin' && targetUser?.role === 'superadmin');
    };

    const canEditUser = (targetUser) => {
        return !(currentUser?.role === 'admin' && targetUser?.role === 'superadmin');
    };

    const handleCloseToast = (event, reason) => {
        if (reason === 'clickaway') return;
        setToast({ ...toast, open: false });
    };

    const loadUsers = async () => {
        try {
            setLoading(true);
            const res = await superadminService.listAdminUsers({
                search: submittedSearch,
                page: page + 1,
                page_size: pageSize
            });
            const data = res?.data;
            if (data?.results) {
                setUsers(data.results);
                setTotal(data.count || 0);
            } else {
                setUsers(data || []);
                setTotal(Array.isArray(data) ? data.length : 0);
            }
        } catch (err) {
            console.error(err);
            setError("Impossible de charger les utilisateurs.");
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDelete = (user) => {
        if (!canDeleteUser(user)) {
            showToast("Un administrateur ne peut pas supprimer un superadmin.", 'error');
            handleCloseMenu();
            return;
        }
        setDeleteUser(user);
        setDeleteOpen(true);
        handleCloseMenu();
    };

    const handleConfirmDelete = async () => {
        if (!deleteUser) return;
        try {
            setDeleteLoading(true);
            await superadminService.deleteAdminUser(deleteUser.id);
            showToast('Utilisateur supprimé.', 'success');
            setDeleteOpen(false);
            setDeleteUser(null);
            loadUsers();
        } catch (err) {
            console.error(err);
            showToast(err.response?.data?.detail || "Erreur lors de la suppression.", 'error');
        } finally {
            setDeleteLoading(false);
        }
    };

    useEffect(() => {
        loadUsers();
    }, [submittedSearch, page, pageSize]);

    const handleViewChange = (event, nextView) => {
        if (nextView !== null) {
            setViewMode(nextView);
        }
    };

    const getRoleColor = (role) => {
        switch (role) {
            case 'superadmin': return theme.colors.error[600];
            case 'admin': return theme.colors.warning[600];
            default: return theme.colors.primary[500];
        }
    };

    const handleOpenCreate = () => {
        setDialogMode('create');
        setFormData({
            first_name: '',
            last_name: '',
            email: '',
            phone: '',
            role: 'admin',
            is_active: true
        });
        setDialogError('');
        setOpenDialog(true);
    };

    const handleOpenEdit = (user) => {
        if (!canEditUser(user)) {
            showToast("Un administrateur ne peut pas modifier un superadmin.", 'error');
            handleCloseMenu();
            return;
        }

        setDialogMode('edit');
        setSelectedUser(user);
        setFormData({
            first_name: user.first_name || '',
            last_name: user.last_name || '',
            email: user.email || '',
            phone: user.phone || '',
            role: user.role || 'admin',
            is_active: user.is_active
        });
        setDialogError('');
        setOpenDialog(true);
        handleCloseMenu();
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedUser(null);
    };

    const handleSaveUser = async () => {
        try {
            setSaving(true);
            setDialogError('');

            if (dialogMode === 'create') {
                await superadminService.createAdminUser(formData);
                showToast('Utilisateur créé avec succès. Un email a été envoyé.', 'success');
            } else {
                await superadminService.updateAdminUser(selectedUser.id, formData);
                showToast('Utilisateur mis à jour avec succès.', 'success');
            }
            setOpenDialog(false);
            loadUsers();
        } catch (err) {
            console.error(err);
            setDialogError(err.response?.data?.email?.[0] || err.response?.data?.detail || "Une erreur est survenue.");
        } finally {
            setSaving(false);
        }
    };

    const handleOpenReset = (user) => {
        setResetUser(user);
        setResetOpen(true);
        handleCloseMenu();
    };

    const handleConfirmReset = async () => {
        if (!resetUser) return;
        try {
            setResetLoading(true);
            await superadminService.resetUserPassword(resetUser.id);
            showToast('Email de réinitialisation envoyé.', 'success');
            setResetOpen(false);
        } catch (err) {
            console.error(err);
            showToast(err.response?.data?.detail || "Erreur lors de l'envoi.", 'error');
        } finally {
            setResetLoading(false);
        }
    };

    // Menu Handlers
    const handleOpenMenu = (event, user) => {
        setAnchorEl(event.currentTarget);
        setMenuUser(user);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
        setMenuUser(null);
    };

    return (
        <Box sx={{ maxWidth: 1600, mx: 'auto', pb: 12 }}>
            {/* Header */}
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: 2,
                mb: 4
            }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 900, color: theme.colors.text.primary, mb: 0.25, fontSize: { xs: '1.25rem', sm: '2.125rem' } }}>
                        Administrateurs
                    </Typography>
                    <Typography variant="body1" sx={{ color: theme.colors.text.secondary, fontSize: { xs: '0.75rem', sm: '1rem' } }}>
                        Gérez les administrateurs et super-administrateurs
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, width: { xs: '100%', sm: 'auto' }, flexWrap: 'wrap' }}>
                    <ToggleButtonGroup
                        value={viewMode}
                        exclusive
                        onChange={handleViewChange}
                        aria-label="view mode"
                        size="small"
                        sx={{
                            bgcolor: 'white',
                            border: `1px solid ${theme.colors.border.light}`,
                            borderRadius: theme.borderRadius.lg,
                            height: { xs: 36, sm: 48 },
                            flex: { xs: 1, sm: 'none' },
                            justifyContent: 'center'
                        }}
                    >
                        <ToggleButton value="card" sx={{ border: 'none', flex: 1 }}><ViewModuleIcon fontSize="small" /></ToggleButton>
                        <ToggleButton value="list" sx={{ border: 'none', flex: 1 }}><ViewListIcon fontSize="small" /></ToggleButton>
                    </ToggleButtonGroup>
                    <Button
                        variant="contained"
                        startIcon={<PersonAddIcon />}
                        onClick={handleOpenCreate}
                        size="small"
                        sx={{
                            fontWeight: 700,
                            borderRadius: theme.borderRadius.lg,
                            textTransform: 'none',
                            px: { xs: 1.5, sm: 3 },
                            flex: { xs: 1, sm: 'none' },
                            height: { xs: 36, sm: 'auto' }
                        }}
                    >
                        Nouvel Admin
                    </Button>
                </Box>
            </Box>

            {/* Search */}
            <Card sx={{ p: 1, mb: 4, borderRadius: theme.borderRadius.xl, boxShadow: theme.boxShadow.sm }}>
                <Box
                    component="form"
                    onSubmit={(e) => { e.preventDefault(); setSubmittedSearch(search); }}
                    sx={{ display: 'flex', alignItems: 'center', width: '100%' }}
                >
                    <InputAdornment position="start" sx={{ ml: 2 }}>
                        <SearchIcon sx={{ color: theme.colors.text.tertiary }} />
                    </InputAdornment>
                    <TextField
                        fullWidth
                        placeholder="Rechercher par nom, email..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        variant="standard"
                        InputProps={{ disableUnderline: true }}
                        sx={{ ml: 1 }}
                    />
                    {search && (
                        <IconButton onClick={() => { setSearch(''); setSubmittedSearch(''); }}>
                            <RefreshIcon />
                        </IconButton>
                    )}
                    <Button type="submit" sx={{ display: 'none' }}>Search</Button>
                </Box>
            </Card>

            {/* Content */}
            <Fade in>
                <Box>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                            <CircularProgress />
                        </Box>
                    ) : error ? (
                        <Alert severity="error">{error}</Alert>
                    ) : users.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 5 }}>
                            <Typography color="text.secondary">Aucun administrateur trouvé.</Typography>
                        </Box>
                    ) : viewMode === 'card' ? (
                        <Grid container spacing={3}>
                            {users.map((user) => (
                                <Grid item xs={12} sm={6} md={4} lg={3} key={user.id}>
                                    <UserCard
                                        user={user}
                                        onEdit={handleOpenEdit}
                                        onDelete={handleOpenDelete}
                                        onResetPassword={handleOpenReset}
                                        canEdit={canEditUser(user)}
                                        canDelete={canDeleteUser(user)}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    ) : (
                        <Card sx={{ borderRadius: theme.borderRadius.xl, overflow: 'hidden', border: `1px solid ${theme.colors.border.light}`, boxShadow: theme.boxShadow.sm }}>
                            <Box sx={{ overflowX: 'auto' }}>
                                <Table sx={{ minWidth: { xs: '100%', md: 650 } }}>
                                    <TableHead sx={{ bgcolor: theme.colors.background.secondary }}>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 700 }}>Utilisateur</TableCell>
                                            <TableCell sx={{ fontWeight: 700, display: { xs: 'none', md: 'table-cell' } }}>Email</TableCell>
                                            <TableCell sx={{ fontWeight: 700, display: { xs: 'none', md: 'table-cell' } }}>Rôle</TableCell>
                                            <TableCell sx={{ fontWeight: 700, display: { xs: 'none', md: 'table-cell' } }}>Statut</TableCell>
                                            <TableCell align="right">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {users.map((user) => (
                                            <TableRow key={user.id} hover>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                        <Avatar sx={{
                                                            display: { xs: 'none', md: 'flex' },
                                                            bgcolor: theme.colors.primary[50],
                                                            color: theme.colors.primary[600],
                                                            fontWeight: 700
                                                        }}>
                                                            {user.first_name?.[0]}
                                                        </Avatar>
                                                        <Typography fontWeight={600}>{user.first_name} {user.last_name}</Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{user.email}</TableCell>
                                                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                                                    <Chip
                                                        label={user.role}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: getRoleColor(user.role) + '20',
                                                            color: getRoleColor(user.role),
                                                            fontWeight: 700,
                                                            textTransform: 'capitalize'
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                                                    <Chip
                                                        label={user.is_active ? 'Actif' : 'Inactif'}
                                                        size="small"
                                                        color={user.is_active ? 'success' : 'default'}
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell align="right">
                                                    <IconButton size="small" onClick={(e) => handleOpenMenu(e, user)}>
                                                        <MoreVertIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Box>
                        </Card>
                    )}

                    {total > 0 && (
                        <Box sx={{
                            position: 'fixed',
                            bottom: 0,
                            right: 0,
                            left: { xs: 0, md: '280px' }, // Offset for sidebar
                            zIndex: 1000,
                            p: 2,
                            display: 'flex',
                            justifyContent: 'center',
                            pointerEvents: 'none' // Box itself doesn't block clicks
                        }}>
                            <Paper
                                elevation={3}
                                sx={{
                                    pointerEvents: 'auto', // Re-enable for pagination
                                    borderRadius: theme.borderRadius.xl,
                                    border: `1px solid ${theme.colors.border.light}`,
                                    overflow: 'hidden',
                                    width: 'fit-content',
                                    maxWidth: '90vw'
                                }}
                            >
                                <TablePagination
                                    component="div"
                                    count={total}
                                    page={page}
                                    onPageChange={(e, newPage) => {
                                        setPage(newPage);
                                        window.scrollTo({ top: 0, behavior: 'smooth' });
                                    }}
                                    rowsPerPage={pageSize}
                                    onRowsPerPageChange={(e) => {
                                        setPageSize(parseInt(e.target.value, 10));
                                        setPage(0);
                                    }}
                                    labelRowsPerPage="Lignes par page"
                                    sx={{ bgcolor: 'white' }}
                                />
                            </Paper>
                        </Box>
                    )}
                </Box>
            </Fade>

            {/* Create/Edit Dialog */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 800 }}>
                    {dialogMode === 'create' ? 'Ajouter un administrateur' : 'Modifier l\'administrateur'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        {dialogError && <Alert severity="error">{dialogError}</Alert>}

                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <TextField
                                    label="Prénom"
                                    fullWidth
                                    value={formData.first_name}
                                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label="Nom"
                                    fullWidth
                                    value={formData.last_name}
                                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                                />
                            </Grid>
                        </Grid>

                        <TextField
                            label="Email"
                            fullWidth
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        />
                        <TextField
                            label="Téléphone"
                            fullWidth
                            value={formData.phone}
                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        />

                        <FormControl fullWidth>
                            <InputLabel>Rôle</InputLabel>
                            <Select
                                value={formData.role}
                                label="Rôle"
                                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                            >
                                <MenuItem value="admin">Administrateur</MenuItem>
                                <MenuItem value="superadmin">Super Administrateur</MenuItem>
                            </Select>
                        </FormControl>

                        {dialogMode === 'edit' && (
                            <FormControl fullWidth>
                                <InputLabel>Statut</InputLabel>
                                <Select
                                    value={formData.is_active}
                                    label="Statut"
                                    onChange={(e) => setFormData({ ...formData, is_active: e.target.value })}
                                >
                                    <MenuItem value={true}>Actif</MenuItem>
                                    <MenuItem value={false}>Inactif</MenuItem>
                                </Select>
                            </FormControl>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleCloseDialog}>Annuler</Button>
                    <Button variant="contained" onClick={handleSaveUser} disabled={saving}>
                        {saving ? <CircularProgress size={24} /> : (dialogMode === 'create' ? 'Créer et Envoyer Email' : 'Enregistrer')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete User Dialog */}
            <Dialog open={deleteOpen} onClose={() => { if (!deleteLoading) { setDeleteOpen(false); setDeleteUser(null); } }} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 800 }}>Supprimer l'utilisateur</DialogTitle>
                <DialogContent>
                    <Typography>
                        Confirmez-vous la suppression de <b>{deleteUser?.first_name} {deleteUser?.last_name}</b> ?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => { setDeleteOpen(false); setDeleteUser(null); }} disabled={deleteLoading}>Annuler</Button>
                    <Button variant="contained" color="error" onClick={handleConfirmDelete} disabled={deleteLoading} startIcon={<DeleteIcon />}>
                        {deleteLoading ? <CircularProgress size={24} color="inherit" /> : 'Supprimer'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Reset Password Dialog */}
            <Dialog open={resetOpen} onClose={() => setResetOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 800 }}>Réinitialiser le mot de passe</DialogTitle>
                <DialogContent>
                    <Typography>
                        Voulez-vous envoyer un email de réinitialisation de mot de passe à <b>{resetUser?.first_name} {resetUser?.last_name}</b> ?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setResetOpen(false)}>Annuler</Button>
                    <Button variant="contained" color="primary" onClick={handleConfirmReset} disabled={resetLoading}>
                        {resetLoading ? <CircularProgress size={24} color="inherit" /> : 'Envoyer'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Action Menu (List View) */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleCloseMenu}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                {menuUser && canEditUser(menuUser) && (
                    <MenuItem onClick={() => handleOpenEdit(menuUser)}>
                        <EditIcon fontSize="small" sx={{ mr: 1.5 }} /> Éditer
                    </MenuItem>
                )}
                <MenuItem onClick={() => menuUser && handleOpenReset(menuUser)}>
                    <LockResetIcon fontSize="small" sx={{ mr: 1.5, color: theme.colors.warning[600] }} /> Réinitialiser MDP
                </MenuItem>
                {menuUser && canDeleteUser(menuUser) && (
                    <MenuItem onClick={() => handleOpenDelete(menuUser)} sx={{ color: theme.colors.error[600] }}>
                        <DeleteIcon fontSize="small" sx={{ mr: 1.5 }} /> Supprimer
                    </MenuItem>
                )}
            </Menu>

            <Snackbar
                open={toast.open}
                autoHideDuration={6000}
                onClose={handleCloseToast}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseToast} severity={toast.severity} sx={{ width: '100%', fontWeight: 600 }} variant="filled">
                    {toast.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default UsersList;
